import { useMemo, useState } from "react";
import { NumField } from "@/components/tool/CalcFields";
import { type HplcColumn, transferMethod } from "@/lib/science/hplc";
import { SciStatGrid } from "@/lib/science/sci-ui";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";

function ColumnFields({
  legend,
  col,
  onChange,
  prefix,
}: {
  legend: string;
  col: { id: string; length: string; particle: string };
  onChange: (patch: Partial<typeof col>) => void;
  prefix: string;
}) {
  return (
    <fieldset className="space-y-3 rounded-xl border border-black/10 p-4">
      <legend className="px-1 text-sm font-medium">{legend}</legend>
      <NumField
        id={`${prefix}-id`}
        label={m.hplc_id()}
        value={col.id}
        onChange={(v) => onChange({ id: v })}
        suffix="mm"
      />
      <NumField
        id={`${prefix}-len`}
        label={m.hplc_length()}
        value={col.length}
        onChange={(v) => onChange({ length: v })}
        suffix="mm"
      />
      <NumField
        id={`${prefix}-part`}
        label={m.hplc_particle()}
        value={col.particle}
        onChange={(v) => onChange({ particle: v })}
        suffix="µm"
      />
    </fieldset>
  );
}

const toCol = (c: {
  id: string;
  length: string;
  particle: string;
}): HplcColumn => ({
  id: Number(c.id),
  length: Number(c.length),
  particle: Number(c.particle),
});

export function HplcTransferCalc() {
  const locale = getLocale();
  const [from, setFrom] = useState({ id: "4.6", length: "150", particle: "5" });
  const [to, setTo] = useState({ id: "2.1", length: "50", particle: "2.6" });
  const [flow, setFlow] = useState("1.5");
  const [injection, setInjection] = useState("20");
  const [gradient, setGradient] = useState("30");

  const out = useMemo(
    () =>
      transferMethod(
        toCol(from),
        toCol(to),
        Number(flow),
        Number(injection),
        Number(gradient),
      ),
    [from, to, flow, injection, gradient],
  );

  const num = (n: number, d = 2) =>
    n.toLocaleString(locale, { maximumFractionDigits: d });

  return (
    <div className="sci-card rounded-2xl space-y-4 p-6 sm:p-8">
      <div className="grid gap-4 sm:grid-cols-2">
        <ColumnFields
          legend={m.hplc_from()}
          col={from}
          prefix="from"
          onChange={(p) => setFrom((s) => ({ ...s, ...p }))}
        />
        <ColumnFields
          legend={m.hplc_to()}
          col={to}
          prefix="to"
          onChange={(p) => setTo((s) => ({ ...s, ...p }))}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <NumField
          id="flow"
          label={m.hplc_flow()}
          value={flow}
          onChange={setFlow}
          suffix="mL/min"
        />
        <NumField
          id="injection"
          label={m.hplc_injection()}
          value={injection}
          onChange={setInjection}
          suffix="µL"
        />
        <NumField
          id="gradient"
          label={m.hplc_gradient()}
          value={gradient}
          onChange={setGradient}
          suffix="min"
        />
      </div>

      {out ? (
        <>
          <div className="sci-result-zone flex items-baseline justify-between gap-4 rounded-xl px-5 py-4">
            <span className="text-sm font-medium text-muted-foreground">
              {m.hplc_new_flow()}
            </span>
            <span
              data-testid="hplc-result"
              className="sci-result font-display text-3xl font-bold sm:text-4xl"
            >
              {num(out.flow, 3)} mL/min
            </span>
          </div>
          <SciStatGrid
            items={[
              {
                label: m.hplc_new_injection(),
                value: `${num(out.injection)} µL`,
              },
              {
                label: m.hplc_new_gradient(),
                value: `${num(out.gradientTime)} min`,
              },
            ]}
          />
        </>
      ) : (
        <p className="text-sm text-muted-foreground">{m.hplc_hint()}</p>
      )}
    </div>
  );
}
